import { displayStatus, type DisplayStatus } from "@dis/shared";
import type { HeaderSearchComponentUsageData, HeaderSearchData } from "@jtms/default-connector";

import { ENTITY_ROUTES } from "../../../core/routing/entity-routes";

export interface SearchRow {
  key: string;
  title: string;
  lines: string[];
  status: DisplayStatus | null;
  updatedAt: string | null;
  link: string[];
  queryParams?: Record<string, string>;
  // Serial number of the matched component this row was found through.
  via?: string;
}

export interface SearchGroup {
  label: string;
  rows: SearchRow[];
  // The table to open with the same search; null where the table can't take a ?query= yet.
  seeAll: string[] | null;
}

type ShipmentData = HeaderSearchData["shipments"][number];

const PER_GROUP = 5;

const isVoid = (record: { currentStatus?: { code: string } | null; statusString?: string | null }) =>
  record.currentStatus?.code === "VOID" || record.statusString === "VOID";

export function searchWords(text: string): string[] {
  return text.trim().split(/\s+/).filter(Boolean);
}

export function shipmentNumbersIn(words: string[]): number[] {
  return words.filter((word) => /^\d{1,9}$/.test(word)).map(Number);
}

const joinParts = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(" · ");

function jobRow(job: HeaderSearchData["jobs"][number], via?: string): SearchRow {
  return {
    key: `job-${job.id}`,
    title: job.jobNumber ?? "TBD",
    lines: [joinParts(job.project?.projectNumber, job.project?.rig?.name)],
    status: displayStatus(job.currentStatus, job.statusString),
    updatedAt: job.updatedAt ?? null,
    link: ENTITY_ROUTES.job(job.id),
    via,
  };
}

const DIRECTION_LABEL: Record<string, string> = { OUTGOING: "Outgoing", INCOMING: "Incoming" };

function shipmentRow(shipment: ShipmentData, via?: string): SearchRow {
  return {
    key: `shipment-${shipment.id}`,
    title: shipment.shipmentNumber != null ? `Shipment ${shipment.shipmentNumber}` : "Shipment (no number)",
    lines: [
      joinParts(
        DIRECTION_LABEL[shipment.direction] ?? shipment.direction,
        shipment.shipmentJobs_on_shipment.map((link) => link.job.jobNumber ?? "TBD").join(", "),
        shipment.project?.projectNumber
      ),
    ],
    status: displayStatus(shipment.currentStatus, shipment.statusString),
    updatedAt: shipment.updatedAt ?? null,
    link: ENTITY_ROUTES.shipment(shipment.id),
    via,
  };
}

function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => !seen.has(item.id) && !!seen.add(item.id));
}

// Jobs and shipments reached through a matched component's Build BOM picks, newest Build first,
// skipping ones the search already matched directly.
function viaComponentRows(data: HeaderSearchData, usage: HeaderSearchComponentUsageData | null, hideVoid: boolean) {
  const jobs: SearchRow[] = [];
  const shipments: SearchRow[] = [];
  if (!usage) return { jobs, shipments };

  const serialById = new Map(data.components.map((component) => [component.id, component.serialNumber]));
  const directJobIds = new Set(data.jobs.map((job) => job.id));
  const directShipmentIds = new Set([...data.shipments, ...data.shipmentsByNumber].map((shipment) => shipment.id));
  const seen = new Set<string>();

  for (const build of usage.builds) {
    const componentId = build.picks.flatMap((line) => line.buildComponentAssignments_on_buildBom)[0]?.componentId;
    const serial = componentId ? serialById.get(componentId) : undefined;
    if (!serial) continue;
    if (build.job && !(hideVoid && isVoid(build.job)) && !directJobIds.has(build.job.id) && !seen.has(build.job.id)) {
      seen.add(build.job.id);
      jobs.push(jobRow(build.job, serial));
    }
    for (const { shipment } of build.shipmentLineItems_on_build) {
      if ((hideVoid && isVoid(shipment)) || directShipmentIds.has(shipment.id) || seen.has(shipment.id)) continue;
      seen.add(shipment.id);
      shipments.push(shipmentRow(shipment, serial));
    }
  }
  return { jobs: jobs.slice(0, PER_GROUP), shipments: shipments.slice(0, PER_GROUP) };
}

// hideVoid also drops VOID jobs and shipments reached through a component; the main query already filters its own rows.
export function searchGroups(
  data: HeaderSearchData,
  usage: HeaderSearchComponentUsageData | null,
  hideVoid: boolean
): SearchGroup[] {
  const via = viaComponentRows(data, usage, hideVoid);
  const groups: SearchGroup[] = [
    {
      label: "Projects",
      seeAll: ["/projects/list"],
      rows: data.projects.map((project) => ({
        key: `project-${project.id}`,
        title: project.projectNumber,
        lines: [joinParts(project.customer?.shortName ?? project.customerString, project.rig?.name)],
        status: displayStatus(project.currentStatus, project.statusString),
        updatedAt: project.updatedAt ?? null,
        link: ENTITY_ROUTES.project(project.id),
      })),
    },
    {
      label: "Jobs",
      seeAll: ["/operations/jobs"],
      rows: [...data.jobs.map((job) => jobRow(job)), ...via.jobs],
    },
    {
      label: "Shipments",
      seeAll: ["/operations/shipments"],
      rows: [
        ...uniqueById([...data.shipmentsByNumber, ...data.shipments])
          .slice(0, PER_GROUP)
          .map((shipment) => shipmentRow(shipment)),
        ...via.shipments,
      ],
    },
    {
      label: "Quotes",
      seeAll: ["/sales/quotes"],
      rows: data.quotes.map((quote) => {
        // Quotes have no page of their own: open the project, or the quotes table when there isn't one.
        const projectId = quote.projectQuotes_on_quote[0]?.project.id;
        return {
          key: `quote-${quote.id}`,
          title: quote.quoteNumber,
          lines: [quote.description ?? ""],
          status: displayStatus(null, quote.statusString),
          updatedAt: quote.updatedAt ?? null,
          link: projectId ? ENTITY_ROUTES.project(projectId) : ["/sales/quotes"],
          queryParams: projectId ? undefined : { query: quote.quoteNumber },
        };
      }),
    },
    {
      label: "Components",
      seeAll: ["/shop/components"],
      rows: data.components.map((component) => ({
        key: `component-${component.id}`,
        title: component.serialNumber,
        lines: [component.part?.partNumber ?? component.partNumberString, component.descriptionDeprecated ?? ""],
        status: displayStatus(component.currentInventoryStatus, component.inventoryStatusString),
        updatedAt: component.updatedAt ?? null,
        link: ["/components", component.serialNumber],
      })),
    },
    {
      label: "Parts",
      seeAll: ["/engineering/parts"],
      rows: data.parts.map((part) => ({
        key: `part-${part.id}`,
        title: part.partNumber,
        lines: [joinParts(part.revision ? `Rev ${part.revision}` : null, part.engineeringDescription)],
        status: displayStatus(null, part.statusString),
        updatedAt: part.updatedAt ?? null,
        link: ENTITY_ROUTES.part(part.id),
      })),
    },
    {
      label: "Containers",
      seeAll: null,
      rows: data.containers.map((container) => ({
        key: `container-${container.id}`,
        title: container.containerNumber,
        lines: [container.description ?? container.sizeType ?? ""],
        status: displayStatus(null, container.containerStatusString),
        updatedAt: container.updatedAt ?? null,
        link: ["/containers", container.containerNumber],
      })),
    },
  ];
  return groups.filter((group) => group.rows.length);
}
